import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import {
    ARTICLE_COMPLAINT_TITLE,
    ARTICLE_DELETED_BY_ADMIN,
    ARTICLE_DELETED_BY_AUTHOR,
    ARTICLE_DELETED_BY_MODERATOR,
    BACK_TO_BLOGS,
    MORE_ARTICLE_SECTIONS,
    PB_RECOMMENDED_ARTICLES,
    POST_REMOVE_COMPLAINTS,
    RELATED_BLOG_ARTICLES,
    STROLLER_STROLLEROPEDIA,
    STROLLEROPEDIA_MORE_ARTICLES,
} from 'mk/autogenerated/translations/Article.491fa0c9319215341aa1a0f6badffe4d'
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import ArticleHeader from 'mk2/apps/blogs/components/ArticleHeader';
import CommentsButton from 'mk2/apps/blogs/components/CommentsButton';
import { CommentsDesign, MAGAZINE_USER } from 'mk2/apps/blogs/constants';
import {
    getArticleTitleAsString,
} from 'mk2/apps/blogs/containers/Article/Article.helpers';
import { Props } from 'mk2/apps/blogs/containers/Article/ArticlePage';
import { revertReplacedTagsOfArticleScripts } from 'mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.helpers';
import Comments from 'mk2/apps/blogs/containers/Comments/Comments';
import { blogsUrl } from 'mk2/apps/blogs/urls';
import { articleUrl as blogsArticleUrl } from 'mk2/apps/blogs/utils';
import StrollersArticleSectionPills from 'mk2/apps/strollers/components/StrollersArticleSectionPills';
import { strollersArticlesOfSectionUrl, strollersUrl } from 'mk2/apps/strollers/urls';
import ReviewBlogPostPreview from 'mk2/apps/wiki/components/ReviewBlogPostPreview';
import { pollPrimaVideoLaunch } from 'mk2/apps/wiki/containers/PrimaEmbed/PrimaEmbed.helpers';
import { Failure } from 'mk2/components/errors/Failure';
import { getArticleBadges, BadgeType, BADGE_CONFIGS } from 'mk2/components/Badge';
import { Btn, BtnType } from 'mk2/components/Btn';
import { DeletedPostAlert } from 'mk2/components/DeletedPostAlert';
import { AdSlot } from 'mk2/containers/AdSlot/AdSlot';
import Complaint from 'mk2/containers/Complaint/Complaint';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import Complaints from 'mk2/containers/Complaints/Complaints';
import Like, { LikeType } from 'mk2/containers/Like/Like';
import { LoadingSwitch } from 'mk2/containers/LoadingSwitch/LoadingSwitch';
import { astToHtml, defaultFilterAdditionalData, FilterAdditionalData } from 'mk2/helpers/article_ast/filters';
import { cacheLast } from 'mk2/helpers/cache';
import { DeletedByRole } from 'mk2/helpers/deleteable';
import { url } from 'mk2/helpers/urls';
import { BlogPostEntity, PostCommentability } from 'mk2/schemas';
import React from 'react';
import styles from './Article.mscss';

const deletedByToText = (deletedByRole: DeletedByRole): string => {
    return {
        [DeletedByRole.DeletedByAuthor]: ARTICLE_DELETED_BY_AUTHOR,
        [DeletedByRole.DeletedByAdmin]: ARTICLE_DELETED_BY_ADMIN,
        [DeletedByRole.DeletedByModerator]: ARTICLE_DELETED_BY_MODERATOR,
    }[deletedByRole];
};

export default class Article extends React.Component<Props> {

    private articleAstToHtmlCache = cacheLast(true);
    private getBadgesCache = cacheLast(true);
    private messageFormTextarea: HTMLTextAreaElement;

    public componentDidMount() {
        const { match: { params: { username, articleSlug } }, onPageView, articlePost } = this.props;
        onPageView(username, articleSlug);
        if (articlePost?.scripts) {
            this.appendArticleScripts(articlePost.scripts);
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>) {
        const { match: { params: { username, articleSlug } }, onPageView, articlePost } = this.props;
        if (prevProps.match.params.articleSlug !== articleSlug) {
            onPageView(username, articleSlug);
        }
        if (prevProps.articlePost?.scripts !== articlePost?.scripts && articlePost?.scripts) {
            this.appendArticleScripts(articlePost.scripts);
        }
    }

    public render() {
        const { loadingState, articlePost } = this.props;

        return (
            <div className={styles.Article}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderLoadSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={!!articlePost}
                />
            </div>
        );
    }

    private load = () => {
        const { match: { params: { username, articleSlug } }, query, onLoad} = this.props;
        const accessKey = query.key;
        onLoad(username, articleSlug, true, query, accessKey);
    };

    private getStrolleropediaSections = (articlePost, suggestedArticles) => {
        const articleBadges = this.getBadgesCache(() => {
            const dict = {};
            for (const blogPostArticle of [...(suggestedArticles || []), articlePost]) {
                const badges = getArticleBadges(blogPostArticle.hashtagItems);
                dict[blogPostArticle.id] = badges.renderedBadges;
            }
            return dict;
        }, articlePost, suggestedArticles);

        const articleHashtagBadgeTypes = getArticleBadges(articlePost.hashtagItems).badgeTypes.filter(
            // excluding VIDEO
            (b) => b !== BadgeType.Video,
        );

        const articleSectionBadgeType = articleHashtagBadgeTypes?.find(type => BADGE_CONFIGS[type]?.sectionSlug);
        const articleSectionSlug = articleSectionBadgeType ? BADGE_CONFIGS[articleSectionBadgeType].sectionSlug : null;
        const breadcrumbInputs = articleSectionBadgeType ? [
            {
                itemUrl: strollersUrl,
                itemLabel: STROLLER_STROLLEROPEDIA,
            },
            {
                itemUrl: url(strollersArticlesOfSectionUrl, { sectionSlug: BADGE_CONFIGS[articleSectionBadgeType].sectionSlug }),
                itemLabel: BADGE_CONFIGS[articleSectionBadgeType].label,
            },
        ] : [];

        return {
            articleBadges,
            articleHashtagBadgeTypes,
            articleSectionSlug,
            breadcrumbInputs,
        };
    };

    private renderLoadSuccess = () => {
        const {
            articlePost, commentsCount, complaintsCount, isMobile, showAds,
            location, history, requestUser, requestUserPermissions, suggestedArticles,
        } = this.props;

        const isAdmin = requestUserPermissions.indexOf('forum.can_manage_forum') >= 0;


        const {
            articleBadges,
            articleHashtagBadgeTypes,
            articleSectionSlug,
            breadcrumbInputs,
        } = this.getStrolleropediaSections(articlePost, suggestedArticles);

        // prepare article body chunks
        let bodyChunks: React.ReactChild[] = null;
        if (articlePost.contentAST) {
            const additionalData: FilterAdditionalData = {
                ...defaultFilterAdditionalData,
                sourceApp: 'blogs',
                sourceLocationPath: location.pathname,
                photos: articlePost.photos,
                customStyles: {
                    header2: styles.Article__content__header2,
                    header3: styles.Article__content__header3,
                    header4: styles.Article__content__header4,
                    paragraph: styles.Article__content__paragraph,
                    orderedlist: styles.Article__content__orderedlist,
                    unorderedlist: styles.Article__content__unorderedlist,
                    articleNote: styles.Article__content__articleNote,
                    instagram: styles.Article__content__instagram,
                    tiktok: styles.Article__content__instagram,
                    prima: styles.Article__content__instagram,
                },
                countTopicContributors: articlePost.countTopicContributors,
                renderLinksWithNofollow: !(articlePost.isPRArticle ||
                    articlePost.isCommercial ||
                    articlePost.user.username === MAGAZINE_USER
                ),
                linkedObjects: articlePost.contentASTAdditionalData.linkedObjects ?? {},
                productBoxes: articlePost.contentASTAdditionalData.productBoxes ?? {},
                extraEntities: {
                    wikiArticleArray: articlePost.extraEntitiesOfArticle,
                    strollerArray: articlePost.extraEntitiesOfStroller,
                    blogPostArray: articlePost.extraEntitiesOfPost,
                },
                hasResponsiveImages: isMobile || requestUserPermissions.indexOf('wiki.can_edit_wiki') >= 0,

                // TODO - automatic suggestedItems are not used yet, will be defined
                // suggestedItems: suggestedArticles.map((blogPostArticle, i) => {
                //     return (
                //         <div key={i} className={styles.Article__suggestedArticle}>
                //             <div className={styles.Article__suggestedArticle__header}>{READ_ALSO}</div>
                //             <ReviewBlogPostPreview
                //                 photo={this.getArticlePhoto(blogPostArticle)}
                //                 title={getArticleTitleAsString(blogPostArticle.titleAST)}
                //                 postUrl={blogsArticleUrl(blogPostArticle)}
                //                 urlDataStoreClick={'suggested_article_clicked'}
                //                 badges={articleBadges[blogPostArticle.id]}
                //                 commentsCount={blogPostArticle.commentsCount}
                //                 likersCount={blogPostArticle.likeable.likesCount}
                //             />
                //         </div>
                //     );
                // }),
            };
            bodyChunks = this.articleAstToHtmlCache(() => astToHtml(articlePost.contentAST, additionalData), articlePost.contentAST);
        }

        return (
            <div className={styles.Article__articlePage}>
                <Complaint
                    id={articlePost.id}
                    entity={ComplaintTargetType.PhotoblogPost}
                    className={styles.Article__complaint}
                    scrollOffset={44 + 44 + 30/* header + 30px spacing */}
                    inForum={false}
                    inGroups={false}
                    title={ARTICLE_COMPLAINT_TITLE}
                />

                {(isAdmin) && (
                    <Complaints
                        className={styles.Article__complaints}
                        id={articlePost.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        deleteLabel={POST_REMOVE_COMPLAINTS}
                        adminTheme={isAdmin}
                    />
                )}

                <div className={styles.Article__header}>
                    <ArticleHeader
                        article={articlePost}
                        hashtagBadgeTypes={articleHashtagBadgeTypes}
                        breadcrumbInputs={breadcrumbInputs}
                    />
                </div>
                {articlePost.deletedAt && (
                    <div className={styles.Article__announcements}>
                        {articlePost.deletedAt && (
                            <DeletedPostAlert
                                heading={deletedByToText(articlePost.deletedByRole)}
                                deletedByRole={articlePost.deletedByRole}
                                deletedBy={articlePost.deletedBy?.username}
                                deletedAt={articlePost.deletedAt}
                                reason={articlePost.deletedReason}
                            />
                        )}
                    </div>
                )}

                <div className={styles.Article__content}>
                    {bodyChunks}
                </div>

                {articlePost.scripts && (
                    <div
                        className={styles.Article__scripts}
                        id="article-scripts"
                    />
                )}

                {suggestedArticles?.length > 0 && (
                    <div className={styles.Article__suggestedArticles}>
                        <h2 className={styles.Article__suggestedArticles__header}>
                            {articleSectionSlug ? RELATED_BLOG_ARTICLES : PB_RECOMMENDED_ARTICLES}
                        </h2>
                        {suggestedArticles.map((blogPostArticle, i) => (
                            <ReviewBlogPostPreview
                                key={i}
                                className={styles.Article__suggestedArticles__article}
                                photo={this.getArticlePhoto(blogPostArticle)}
                                title={getArticleTitleAsString(blogPostArticle.titleAST)}
                                postUrl={blogsArticleUrl(blogPostArticle)}
                                urlDataStoreClick={'suggested_article_clicked'}
                                badges={articleBadges[blogPostArticle.id]}
                                commentsCount={blogPostArticle.commentsCount}
                                likersCount={blogPostArticle.likeable.likesCount}
                            />
                        ))}
                        {articleSectionSlug &&
                            <Btn
                                type={BtnType.BlueOutline}
                                label={STROLLEROPEDIA_MORE_ARTICLES}
                                link={url(strollersArticlesOfSectionUrl, { sectionSlug: articleSectionSlug })}
                                className={styles.Article__suggestedArticles__button}
                            />
                        }
                    </div>
                )}

                {articleSectionSlug && (
                    <div className={styles.Article__strollerArticleSections}>
                        <h2>{MORE_ARTICLE_SECTIONS}</h2>
                        <StrollersArticleSectionPills />
                    </div>
                )}

                <div className={styles.Article__footer}>
                    <div className={styles.Article__footer__likes}>
                        <Like
                            entity="photoblog.post"
                            id={articlePost.id}
                            type={LikeType.IconCount}
                            className={styles.Article__footer__likes__like}
                        />
                    </div>
                    <div className={styles.Article__footer__comments}>
                        <CommentsButton
                            commentsCount={commentsCount}
                            className={cx(
                                styles.Article__footer__comments__commentsButton,
                                complaintsCount && styles['Article__footer__comments__commentsButton--has-complaints'],
                            )}
                            onClick={this.handleOnComments}
                        />
                    </div>
                </div>

                <Comments
                    key="article-comments"
                    parentPost={articlePost}
                    count={commentsCount}
                    design={CommentsDesign.Normal}
                    moderators={[]}   // TBD
                    location={location}
                    history={history}
                    showFakeTextareaIfCantAdd={articlePost.commentability === PostCommentability.CommentableByAuthenticated && !isAuthenticated(requestUser)}
                    hasBlueToolbar
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    isMobile={isMobile}
                    isPreloaded
                    onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                />

                {showAds && (
                    isMobile ? (
                        <AdSlot
                            zone="Blogs Article - Rectangle 3 (Mobile)"
                            location={location}
                            marginTopWithAd={10}
                        />
                    ) : (
                        <div className={styles.Article__adSlotWrap}>
                            <AdSlot
                                zone="Blogs Article - Rectangle 1 (Desktop)"
                                location={location}
                                marginTopWithAd={30}
                            />
                        </div>
                    )
                )}
            </div>
        );
    };

    private renderFailure = () => {
        const { errorMessage, staticContext } = this.props;
        return (
            <Failure
                errorMessage={errorMessage}
                staticContext={staticContext}
                onRetry={this.load}
                backButton={
                    <Btn
                        type={BtnType.Blue}
                        label={BACK_TO_BLOGS}
                        icon={faArrowLeft}
                        link={blogsUrl}
                    />
                }
            />
        );
    };

    private appendArticleScripts(scripts: string) {
        // remove old scripts from previous articles
        const old = document.querySelectorAll('[data-article-script]');
        old.forEach(el => el.remove());

        const html = revertReplacedTagsOfArticleScripts(scripts);
        const container = document.createElement('div');
        container.innerHTML = html;

        const target = document.getElementById('article-scripts');
        if (!target) return;
        target.innerHTML = '';

        // append all non-script elements first (like <div data-video-embed>)
        container.childNodes.forEach(node => {
            if (node.nodeName.toLowerCase() !== 'script') {
                target.appendChild(node.cloneNode(true));
            }
        });

        // then append scripts after the elements are in the DOM
        container.querySelectorAll('script').forEach(script => {
            const src = script.getAttribute('src');
            const newScript = document.createElement('script');

            if (src) {
                // load external script dynamically (so it sees the inserted elements)
                newScript.src = src;
                newScript.type = script.type || 'text/javascript';
                newScript.async = true;
            } else if (script.textContent) {
                // handle inline script content
                newScript.textContent = script.textContent;
            }

            newScript.setAttribute('data-article-script', '');
            target.appendChild(newScript);
        });

        pollPrimaVideoLaunch('article-scripts', 20, 500);
    }

    private handleOnComments = () => {
        if (this.messageFormTextarea) {
            this.messageFormTextarea.focus();
        }
    };

    private handleSetMessageFormTextareaRef = (ref: any) => {
        this.messageFormTextarea = ref;
    };

    private getArticlePhoto = (blogPostArticle: BlogPostEntity) => {
        return this.props.suggestedArticlesHomepagePhotos?.[blogPostArticle.id] || blogPostArticle.photos?.[0];
    };
}
